import React, { useState, useEffect } from 'react';
import { Button, Dialog, Flex, IconButton, Select, Text, TextField, Tooltip } from '@radix-ui/themes';
import { BiRefresh } from 'react-icons/bi';
import { MdOutlineEdit } from 'react-icons/md';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axiosInstance from '../../config/axiosConfig';

const CreateCaregiver = ({ initialValues = null, roleId, onCaregiverCreated, showToast }) => {
    const isEditMode = initialValues !== null;
    const [isOpen, setIsOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [departments, setDepartments] = useState([]);
    const isDepartmentAccount = String(roleId || localStorage.getItem('roleId')) === '3';
    const currentUserId = localStorage.getItem('userId');

    const validationSchema = Yup.object({
        firstName: Yup.string().required('First Name is required'),
        lastName: Yup.string().required('Last Name is required'),
        username: Yup.string().required('Username is required'),
        department: Yup.string().required('Department is required'),
        employeeId: Yup.string().required('Employee ID is required'),
        password: isEditMode
            ? Yup.string()
            : Yup.string().required('Password is required'),
    });

    const formik = useFormik({
        initialValues: {
            firstName: initialValues?.firstName || initialValues?.first_name || '',
            lastName: initialValues?.lastName || initialValues?.last_name || '',
            username: initialValues?.userName || initialValues?.username || '',
            password: '',
            department: String(initialValues?.supervisorId || initialValues?.supervisor || initialValues?.department || ''),
            employeeId: initialValues?.employeeId || initialValues?.employee_id || '',
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            setIsSubmitting(true);
            try {
                const caregiverData = {
                    token: isEditMode ? initialValues.token : null,
                    firstName: values.firstName,
                    lastName: values.lastName,
                    userName: values.username,
                    supervisor: values.department,
                    employeeId: values.employeeId,
                    role: 'Caregiver',
                    ...(values.password ? { password: values.password } : {}),
                };

                const endpoint = isEditMode ? '/user/update-user' : '/account/register-caregiver';
                const response = await axiosInstance.post(endpoint, caregiverData);

                if (response.data.result) {
                    const action = isEditMode ? 'updated' : 'created';
                    if (onCaregiverCreated) await onCaregiverCreated();
                    if (showToast) showToast(`Caregiver ${action} successfully`, 'success');
                    setIsOpen(false);
                } else {
                    const action = isEditMode ? 'update' : 'create';
                    console.error(`Failed to ${action} caregiver`, response.data);
                    if (showToast) showToast(response.data.errorResponse?.message || `Failed to ${action} caregiver`, 'error');
                }
            } catch (error) {
                console.error('Error submitting caregiver:', error);
                const action = isEditMode ? 'updating' : 'creating';
                if (showToast) showToast(`An error occurred while ${action} caregiver`, 'error');
            } finally {
                setIsSubmitting(false);
            }
        },
    });

    useEffect(() => {
        // Fetch departments only when dialog is opened
        if (isOpen) {
            const fetchDepartments = async () => {
                try {
                    const response = await axiosInstance.get('/user/get-departments');
                    if (response.data.result) {
                        const departmentList = response.data.result || [];
                        setDepartments(departmentList);
                        if (!isEditMode && isDepartmentAccount && currentUserId) {
                            const currentDepartment = departmentList.find((dept) => String(dept.key) === String(currentUserId));
                            if (currentDepartment) {
                                formik.setFieldValue('department', String(currentDepartment.key));
                            }
                        }
                    } else {
                        console.error('Failed to fetch departments:', response.data);
                        if (showToast) showToast('Failed to fetch departments', 'error');
                    }
                } catch (error) {
                    console.error('Error fetching departments:', error);
                    if (showToast) showToast('Error fetching departments', 'error');
                }
            };
            fetchDepartments();
        }
    }, [isOpen, showToast, isEditMode, isDepartmentAccount, currentUserId, formik.setFieldValue]);

    const generatePassword = () => {
        const length = 6;
        const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*';
        let newPassword = '';
        for (let i = 0; i < length; i++) {
            const randomIndex = Math.floor(Math.random() * charset.length);
            newPassword += charset[randomIndex];
        }
        formik.setFieldValue('password', newPassword);
    };

    return (
        <Dialog.Root
            open={isOpen}
            onOpenChange={(open) => setIsOpen(open)}
        >
            {isEditMode ? (
                <Dialog.Trigger>
                    <IconButton variant="outline" onClick={() => setIsOpen(true)}>
                        <MdOutlineEdit width="18" height="18" />
                    </IconButton>
                </Dialog.Trigger>
            ) : (
                <Dialog.Trigger>
                    <Button onClick={() => setIsOpen(true)}>Add Caregiver</Button>
                </Dialog.Trigger>
            )}

            <Dialog.Content maxWidth="450px">
                <Dialog.Title>{isEditMode ? "Manage Caregiver Details" : "Create Caregiver"}</Dialog.Title>

                <form onSubmit={formik.handleSubmit}>
                    <Flex direction="column" gap="3">
                        {/* First Name & Last Name */}
                        <Flex gap="3">
                            <label className="w-[50%]">
                                <Text as="div" size="2" mb="1" weight="bold">
                                    First Name
                                </Text>
                                <TextField.Root
                                    name="firstName"
                                    value={formik.values.firstName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="Enter first name"
                                />
                                {formik.touched.firstName && formik.errors.firstName && (
                                    <Text size="1" color="red">{formik.errors.firstName}</Text>
                                )}
                            </label>
                            <label className="w-[50%]">
                                <Text as="div" size="2" mb="1" weight="bold">
                                    Last Name
                                </Text>
                                <TextField.Root
                                    name="lastName"
                                    value={formik.values.lastName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="Enter last name"
                                />
                                {formik.touched.lastName && formik.errors.lastName && (
                                    <Text size="1" color="red">{formik.errors.lastName}</Text>
                                )}
                            </label>
                        </Flex>

                        {/* Department & Employee ID */}
                        <Flex gap="3">
                            <label className="w-[50%]">
                                <Text as="div" size="2" mb="1" weight="bold">
                                    Department
                                </Text>
                                <Select.Root
                                    name="department"
                                    value={formik.values.department}
                                    onValueChange={(value) => formik.setFieldValue('department', value)}
                                    onBlur={formik.handleBlur}
                                    disabled={isDepartmentAccount && !isEditMode}
                                >
                                    <Select.Trigger style={{ width: '100%' }} placeholder="Select department" />
                                    <Select.Content position="popper">
                                        <Select.Group>
                                            {departments.map((dept) => (
                                                <Select.Item key={dept.key} value={String(dept.key)}>
                                                    {dept.value}
                                                </Select.Item>
                                            ))}
                                        </Select.Group>
                                    </Select.Content>
                                </Select.Root>
                                {formik.touched.department && formik.errors.department && (
                                    <Text size="1" color="red">{formik.errors.department}</Text>
                                )}
                            </label>
                            <label className="w-[50%]">
                                <Text as="div" size="2" mb="1" weight="bold">
                                    Employee ID
                                </Text>
                                <TextField.Root
                                    name="employeeId"
                                    value={formik.values.employeeId}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="Enter employee ID"
                                />
                                {formik.touched.employeeId && formik.errors.employeeId && (
                                    <Text size="1" color="red">{formik.errors.employeeId}</Text>
                                )}
                            </label>
                        </Flex>

                        {/* Username & Password */}
                        <Flex gap="3">
                            <label className="w-[50%]">
                                <Text as="div" size="2" mb="1" weight="bold">
                                    Username
                                </Text>
                                <TextField.Root
                                    name="username"
                                    value={formik.values.username}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="Enter username"
                                    disabled={isEditMode}
                                />
                                {formik.touched.username && formik.errors.username && (
                                    <Text size="1" color="red">{formik.errors.username}</Text>
                                )}
                            </label>
                            <label className="w-[50%]">
                                <Text as="div" size="2" mb="1" weight="bold">
                                    Password
                                </Text>
                                <Flex gap="2">
                                    <TextField.Root
                                        name="password"
                                        value={formik.values.password}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder={isEditMode ? "Blank keeps current" : "Enter password"}
                                    />
                                    <Tooltip content="Generate Password">
                                        <IconButton type="button" variant="solid" onClick={generatePassword}>
                                            <BiRefresh width="18" height="18" />
                                        </IconButton>
                                    </Tooltip>
                                </Flex>
                                {formik.touched.password && formik.errors.password && (
                                    <Text size="1" color="red">{formik.errors.password}</Text>
                                )}
                            </label>
                        </Flex>
                    </Flex>

                    {/* Buttons */}
                    <Flex gap="3" mt="4" justify="end">
                        <Button
                            type="button"
                            variant="soft"
                            color="gray"
                            onClick={() => setIsOpen(false)}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isSubmitting}
                        >
                            {isEditMode ? "Update" : "Save"}
                        </Button>
                    </Flex>
                </form>
            </Dialog.Content>
        </Dialog.Root>
    );
};

export default CreateCaregiver;
